import React, { useState, useRef, useCallback } from 'react';

import { faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';

import { titleize } from '@admin/components/helpers/titleize';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import { useMessagesQuery, MessagesQuery } from '@admin/schema';

type MessageFromQuery = NonNullable<MessagesQuery['paginatedMessages']>['results'][0];

interface MessageWithPosition extends MessageFromQuery {
  position?: number;
}

interface MessageHistoryPopoverProps {
  recipientType: string;
  recipientId: string;
}

const MAX_TEXT_DISPLAY = 160;

const addPosition = (messages: MessageFromQuery[]): MessageWithPosition[] => {
  let count = 0;
  let previousKind: string | undefined;

  return messages.map((message) => {
    if (message.kind === previousKind) {
      count += 1;
    } else {
      count = 0;
    }

    const messageWithPosition: MessageWithPosition = { ...message, position: count };
    previousKind = message.kind;
    return messageWithPosition;
  });
};

const getRowClasses = (message: MessageWithPosition): string => {
  const parity = (message.position || 0) % 2 ? 'odd-row' : 'even-row';
  const direction = message.kind === 'inbound' ? 'inbound-row' : 'outbound-row';
  return `${parity} ${direction}`;
};

const truncateText = (text: string, maxLength: number): string => {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const formatTimeAgo = (dateString: string): string => {
  try {
    const date = DateTime.fromISO(dateString);
    return date.toRelative() || '';
  } catch {
    return '';
  }
};

export const MessageHistoryPopover: React.FC<MessageHistoryPopoverProps> = ({ recipientType, recipientId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data, loading } = useMessagesQuery({
    variables: {
      recipientType,
      recipientIds: [recipientId],
      page: currentPage,
      perPage: 5,
    },
    skip: !shouldFetch,
    client,
  });

  const startHideTimer = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setShouldFetch(false); // Reset fetch state when popover closes
      setCurrentPage(1); // Reset to first page when closing
    }, 300);
  }, []);

  const cancelHideTimer = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleEnterIcon = useCallback(() => {
    if (!shouldFetch) {
      setShouldFetch(true);
    }
    cancelHideTimer();
    setIsOpen(true);
  }, [shouldFetch, cancelHideTimer]);

  const handleLeaveIcon = useCallback(() => {
    startHideTimer();
  }, [startHideTimer]);

  const handleEnterPopover = useCallback(() => {
    cancelHideTimer();
  }, [cancelHideTimer]);

  const handleLeavePopover = useCallback(() => {
    startHideTimer();
  }, [startHideTimer]);

  const messages = data?.paginatedMessages?.results ? addPosition(data.paginatedMessages.results) : [];
  const hasMessages = messages.length > 0;
  const pagination = data?.paginatedMessages?.pagination;
  const hasPrevPage = currentPage > 1;
  const hasNextPage = pagination && currentPage < pagination.totalPages;

  return (
    <span
      onMouseEnter={handleEnterIcon}
      onMouseLeave={handleLeaveIcon}
      onClick={handleEnterIcon}
      className="message-history-trigger"
      role="button"
      tabIndex={0}
      aria-label="Message history"
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleEnterIcon();
        }
      }}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <FontAwesomeIcon icon={faList} />

      {isOpen && (
        <div
          className="popover message-history-popover left-top"
          onMouseEnter={handleEnterPopover}
          onMouseLeave={handleLeavePopover}
          style={{
            position: 'absolute',
            zIndex: 1060,
            left: '-450px',
            top: '0',
            display: 'block',
            width: '420px',
          }}
        >
          <div className="arrow" />
          <h3 className="popover-title">Message History</h3>
          <div className="popover-content">
            <Spinner visible={loading} />

            {!loading && (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {hasMessages ? (
                  <table className="message-history-popover-table table">
                    <tbody>
                      {messages.map((message) => (
                        <tr key={message.id} className={getRowClasses(message)}>
                          <td className="break-word col-sm-7 text-left">
                            {truncateText(message.body, MAX_TEXT_DISPLAY)}
                          </td>
                          <td className="col-sm-5 nowrap text-right">
                            <span className="text-capitalize">{titleize(message.kind)}</span>
                            <span className="time-ago"> {formatTimeAgo(message.createdAt)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center">No messages found</div>
                )}

                {hasMessages && (
                  <div className="mar-top">
                    {/* Pagination Controls */}
                    <div className="text-center mar-btm-sm">
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          className="btn btn-default"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={!hasPrevPage || loading}
                        >
                          ← Prev
                        </button>
                        <span className="btn btn-default disabled">
                          Page {currentPage} of {pagination?.totalPages || 1}
                        </span>
                        <button
                          className="btn btn-default"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={!hasNextPage || loading}
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  );
};
